import React, { useState, useEffect } from "react";
import {
  Heart,
  Trash2,
  Share2,
  Filter,
  SortDesc,
  ShoppingCart,
  ArrowUpRight,
  Tag,
  ChevronDown,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";

// Couleurs du thème
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const LikeProduitPage = ({ acces }) => {
  const navigation = useNavigate();
  const [likedProducts, setLikedProducts] = useState([]);
  const [sortBy, setSortBy] = useState("dateAdded");
  const [filterCategory, setFilterCategory] = useState("all");
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const DATA_Categories = useSelector((state) => state.products.categories);
  const API_URL = process.env.REACT_APP_Backend_Url;
  const userId = JSON.parse(localStorage.getItem("userEcomme"))?.id;
  // État pour la modal de confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Charger les produits likés au montage du composant
  useEffect(() => {
    fetchLikedProducts();
  }, []);

  const fetchLikedProducts = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/likes/user/${userId}`);
      setLikedProducts(response.data.map((like) => like.produit));
      setIsLoading(false);
    } catch (err) {
      setError("Erreur lors du chargement des favoris");
      setIsLoading(false);
      console.error("Erreur:", err);
    }
  };

  const showToast = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  // Fonction pour ouvrir la modal de confirmation
  const confirmRemoveFromLiked = (product) => {
    setProductToDelete(product);
    setShowConfirmModal(true);
  };

  // Fonction pour supprimer le produit après confirmation
  const removeFromLiked = async () => {
    if (!productToDelete) return;

    try {
      await axios.delete(`${API_URL}/likes/${userId}/${productToDelete._id}`);
      setLikedProducts((prev) =>
        prev.filter((product) => product._id !== productToDelete._id)
      );
      showToast(`"${productToDelete.name}" a été retiré de vos favoris`);
      setShowConfirmModal(false);
      setProductToDelete(null);
    } catch (err) {
      showToast("Erreur lors de la suppression du favori");
      console.error("Erreur:", err);
    }
  };

  const shareProduct = (product) => {
    const productDetailUrl = `${window.location.origin}/ProduitDétail/${product._id}`;

    if (navigator.share) {
      navigator
        .share({
          title: product.name,
          text: product.description,
          url: productDetailUrl,
        })
        .then(() => showToast("Produit partagé avec succès !"))
        .catch((err) => console.error("Erreur lors du partage:", err));
    } else {
      // Copier le lien dans le presse-papier si le partage n'est pas disponible
      navigator.clipboard
        .writeText(productDetailUrl)
        .then(() => showToast("Lien copié dans le presse-papier !"))
        .catch((err) =>
          showToast("Le partage n'est pas disponible sur votre appareil")
        );
    }
  };

  const addToCart = async (product) => {
    // Implémentez ici votre logique d'ajout au panier
    showToast(`${product.name} ajouté au panier !`);
  };

  // Fonction pour nettoyer le HTML et limiter le texte
  const formatDescription = (htmlString, maxLength = 100) => {
    // Supprimer toutes les balises HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    let text = tempDiv.textContent || tempDiv.innerText || "";

    // Supprimer les espaces multiples et les retours à la ligne
    text = text.replace(/\s+/g, " ").trim();

    // Limiter la longueur et ajouter des points de suspension si nécessaire
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  // Calculer le pourcentage de réduction
  const calculateDiscount = (price, promoPrice) => {
    if (!promoPrice || price <= promoPrice) return null;
    const discount = Math.round(((price - promoPrice) / price) * 100);
    return discount > 0 ? discount : null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div
          className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
          style={{ borderColor: COLORS.green }}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500 bg-gray-50">
        {error}
      </div>
    );
  }

  // Filtrer les produits selon la catégorie
  const filteredProducts = likedProducts.filter((product) => {
    if (filterCategory === "all") return true;

    // Vérifier si ClefCategorie correspond à l'_id de la catégorie sélectionnée
    return product.ClefCategorie === filterCategory;
  });

  // Trier les produits
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === "price") return a?.prix - b?.prix;
    if (sortBy === "priceDesc") return b?.prix - a?.prix;
    if (sortBy === "name") return a?.name.localeCompare(b?.name);
    if (sortBy === "nameDesc") return b?.name.localeCompare(a?.name);
    // Par défaut, trier par date d'ajout (du plus récent au plus ancien)
    return (
      new Date(b?.dateCreating || b?.dateAdded) -
      new Date(a?.dateCreating || a?.dateAdded)
    );
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.paleGreen }}>
      {/* Notification Toast */}
      <div
        className="fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 z-50 text-white"
        style={{
          backgroundColor: COLORS.green,
          transform: showNotification ? "translateY(0)" : "translateY(-100%)",
          opacity: showNotification ? 1 : 0,
        }}
      >
        {notificationMessage}
      </div>

      {/* Modal de confirmation */}
      {showConfirmModal && productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full"
            style={{ borderTop: `4px solid ${COLORS.orange}` }}
          >
            <div className="flex justify-between items-start mb-4">
              <h3
                className="text-xl font-medium"
                style={{ color: COLORS.darkGreen }}
              >
                Retirer des favoris ?
              </h3>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setProductToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="h-16 w-16 rounded overflow-hidden mr-4 flex-shrink-0">
                  <img
                    src={productToDelete.image1}
                    alt={productToDelete.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium">{productToDelete.name}</p>
                  <p className="text-sm text-gray-500">
                    {productToDelete.prixPromo ? (
                      <>
                        <span style={{ color: COLORS.orange }}>
                          {productToDelete.prixPromo.toLocaleString()} €
                        </span>
                        <span className="line-through ml-2">
                          {productToDelete.prix.toLocaleString()} €
                        </span>
                      </>
                    ) : (
                      <span>{productToDelete.prix.toLocaleString()} €</span>
                    )}
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-2">
                Êtes-vous sûr de vouloir retirer ce produit de vos favoris ?
              </p>
              <p className="text-gray-600">
                Vous pourrez toujours l'ajouter à nouveau plus tard si vous
                changez d'avis. 💫
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setProductToDelete(null);
                }}
                className="flex-1 py-2 px-4 border rounded-lg font-medium"
                style={{ borderColor: COLORS.lightGray, color: COLORS.gray }}
              >
                Garder dans mes favoris
              </button>
              <button
                onClick={removeFromLiked}
                className="flex-1 py-2 px-4 rounded-lg text-white font-medium"
                style={{ backgroundColor: COLORS.orange }}
              >
                Retirer des favoris
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="shadow" style={{ backgroundColor: COLORS.white }}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1
            className="text-3xl font-bold"
            style={{ color: COLORS.darkGreen }}
          >
            Mes Favoris
          </h1>
          <p className="mt-2 text-gray-600">
            {likedProducts.length} produit
            {likedProducts.length !== 1 ? "s" : ""} dans vos favoris
          </p>
        </div>
      </div>

      {/* Filtres et tri */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-8">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white mb-4"
            style={{ backgroundColor: COLORS.green }}
          >
            <Filter size={18} />
            <span>Filtres et tri</span>
            <ChevronDown
              size={18}
              className={`transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>

          {showFilters && (
            <div className="bg-white p-4 rounded-lg shadow-md mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: COLORS.darkGreen }}
                >
                  Catégorie
                </label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2 focus:ring focus:ring-opacity-50"
                  style={{
                    borderColor: COLORS.lightGray,
                    outlineColor: COLORS.green,
                  }}
                >
                  <option value="all">Toutes les catégories</option>
                  {DATA_Categories &&
                    DATA_Categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: COLORS.darkGreen }}
                >
                  Trier par
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2 focus:ring focus:ring-opacity-50"
                  style={{
                    borderColor: COLORS.lightGray,
                    outlineColor: COLORS.green,
                  }}
                >
                  <option value="dateAdded">
                    Date d'ajout (récent → ancien)
                  </option>
                  <option value="price">Prix (croissant)</option>
                  <option value="priceDesc">Prix (décroissant)</option>
                  <option value="name">Nom (A → Z)</option>
                  <option value="nameDesc">Nom (Z → A)</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Liste des produits */}
        {sortedProducts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="flex flex-col items-center justify-center">
              <Heart
                size={64}
                className="mb-4"
                style={{ color: COLORS.gray }}
              />
              <p className="text-gray-500 text-lg mb-4">
                Aucun produit dans vos favoris
              </p>
              <button
                onClick={() => navigation("/")}
                className="px-6 py-2 rounded-lg text-white"
                style={{ backgroundColor: COLORS.green }}
              >
                Découvrir des produits
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedProducts.map((product) => {
              const discount = calculateDiscount(
                product?.prix,
                product?.prixPromo
              );
              if (!product?.name) {
                return null;
              } else
                return (
                  <div
                    key={product?._id}
                    className="bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-xl"
                    onMouseEnter={() => setHoveredProduct(product?._id)}
                    onMouseLeave={() => setHoveredProduct(null)}
                  >
                    <div className="relative">
                      <img
                        src={product?.image1}
                        alt={product?.name}
                        className="w-full h-56 object-cover"
                      />

                      {/* Badge de promotion */}
                      {discount && (
                        <div
                          className="absolute top-2 left-2 px-2 py-1 rounded text-white text-sm font-bold"
                          style={{ backgroundColor: COLORS.orange }}
                        >
                          -{discount}%
                        </div>
                      )}

                      {/* Actions sur survol */}
                      <div
                        className={`absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center space-x-4 transition-opacity duration-300 ${
                          hoveredProduct === product?._id
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      >
                        <button
                          onClick={() => confirmRemoveFromLiked(product)}
                          className="p-3 rounded-full text-white hover:bg-opacity-90 transition-colors"
                          style={{ backgroundColor: COLORS.orange }}
                          title="Retirer des favoris"
                        >
                          <Trash2 size={20} />
                        </button>
                        <button
                          onClick={() => shareProduct(product)}
                          className="p-3 rounded-full text-white hover:bg-opacity-90 transition-colors"
                          style={{ backgroundColor: COLORS.darkGreen }}
                          title="Partager le produit"
                        >
                          <Share2 size={20} />
                        </button>
                        <button
                          onClick={() =>
                            navigation(`/ProduitDétail/${product?._id}`)
                          }
                          className="p-3 rounded-full text-white hover:bg-opacity-90 transition-colors"
                          style={{ backgroundColor: COLORS.green }}
                          title="Voir le produit"
                        >
                          <ShoppingCart size={20} />
                        </button>
                      </div>
                    </div>

                    <div className="p-4">
                      {/* Marque */}
                      {product?.marque && (
                        <p
                          className="text-xs uppercase tracking-wider"
                          style={{ color: COLORS.gray }}
                        >
                          {product?.marque}
                        </p>
                      )}

                      {/* Nom du produit */}
                      <h3 className="text-lg font-semibold mt-1 mb-2">
                        {product?.name}
                      </h3>

                      {/* Prix */}
                      <div className="flex items-center mt-2">
                        {product?.prixPromo &&
                        product?.prixPromo < product?.prix ? (
                          <>
                            <p
                              className="font-bold text-lg"
                              style={{ color: COLORS.orange }}
                            >
                              {product?.prixPromo?.toLocaleString()} €
                            </p>
                            <p className="ml-2 text-gray-500 line-through text-sm">
                              {product?.prix?.toLocaleString()} €
                            </p>
                          </>
                        ) : (
                          <p
                            className="font-bold text-lg"
                            style={{ color: COLORS.darkGreen }}
                          >
                            {product?.prix.toLocaleString()} €
                          </p>
                        )}
                      </div>

                      {/* Catégorie */}
                      {product?.category && (
                        <div className="mt-2 flex items-center">
                          <Tag
                            size={14}
                            className="mr-1"
                            style={{ color: COLORS.gray }}
                          />
                          <p className="text-sm" style={{ color: COLORS.gray }}>
                            {product?.category}
                          </p>
                        </div>
                      )}

                      {/* Description */}
                      <p className="text-sm text-gray-600 mt-3">
                        {formatDescription(product?.description || "")}
                      </p>

                      {/* Stock et bouton Voir plus */}
                      <div className="mt-4 flex justify-between items-center">
                        <span
                          className="text-sm"
                          style={{
                            color: product?.quantite > 0 ? COLORS.green : "red",
                          }}
                        >
                          {product?.quantite > 0
                            ? `En stock (${product?.quantite})`
                            : "Rupture de stock"}
                        </span>
                        <button
                          onClick={() =>
                            navigation(`/ProduitDétail/${product?._id}`)
                          }
                          className="flex items-center text-sm font-medium transition-colors"
                          style={{ color: COLORS.darkGreen }}
                        >
                          Voir plus
                          <ArrowUpRight size={16} className="ml-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LikeProduitPage;
